import { _ as __decorate, f as __metadata, B as BaseComponent, c as TIMELINE_POINT_COLORS, e as TIMELINE_VARIANTS, d as TIMELINE_POINT_LIST_TAGS } from '../types-BINffz1b.js';
import { css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { unsafeStatic, html as html$1 } from 'lit/static-html.js';
import 'lit/directive.js';

var styles = css`*,
*::before,
*::after {
  box-sizing: border-box;
}

:host {
  display: block;
  --it-timeline-point-content-flex-basis: 0%;
  --it-timeline-point-margin-bottom: 24px;
  --it-timeline-point-content-z-index: 1;
  --it-timeline-point-after-content: '';
  --it-timeline-point-stack-mobile-after-content: none;
}

@media (max-width: 767.98px) {
  :host([stack-mobile]) ::slotted(it-timeline-point) {
    --it-timeline-point-content-flex-basis: 100%;
    --it-timeline-point-content-flex-wrap: wrap;
    --it-timeline-point-margin-top: 24px;
    --it-timeline-point-content-z-index: 2;
    --it-timeline-point-after-content: none;
    --it-timeline-point-stack-mobile-after-content: '';
    --it-timeline-point-stack-mobile-after-top: 0;
  }
}

.it-timeline-wrapper {
  display: block;
}

.it-timeline-wrapper.it-timeline-wrapper--classic .row {
  position: relative;
  padding-top: 64px;
}
.it-timeline-wrapper.it-timeline-wrapper--classic .row::after {
  position: absolute;
  top: -16px;
  bottom: -16px;
  left: 40px;
  width: var(--bsi-border-thick, 4px);
  background: var(--bsi-color-background-primary-deep);
  content: "";
}
@media (min-width: 992px) {
  .it-timeline-wrapper.it-timeline-wrapper--classic .row::after {
    left: calc(50% - 4px);
  }
}

::slotted(it-timeline-element) {
  position: relative;
  display: block;
  width: 100%;
}

@media (min-width: 992px) {
  ::slotted(it-timeline-element) {
    width: 50%;
  }
  ::slotted(it-timeline-element:nth-child(odd)) {
    --it-timeline-now-label-base-displacement: 68px;
    --it-timeline-now-label-left: -26px;
    --it-timeline-now-label-right: calc(-1 * var(--it-timeline-now-label-base-displacement));
    --it-timeline-pin-left: auto;
    --it-timeline-pin-right: -22px;
    --it-timeline-pin-direction: row-reverse;
    --it-timeline-pin-icon-mr: 0;
    --it-timeline-pin-icon-ml: 16px;
    --it-timeline-pin-arrow-left: auto;
    --it-timeline-pin-arrow-right: -4px;
    --it-timeline-content-padding: 18px 40px 18px 18px;
  }
  ::slotted(it-timeline-element:nth-child(even)) {
    margin-left: 50%;
    --it-timeline-now-label-base-displacement: 80px;
    --it-timeline-now-label-left: calc(-1 * var(--it-timeline-now-label-base-displacement));
    --it-timeline-now-label-right: unset;
    --it-timeline-pin-left: -26px;
    --it-timeline-pin-right: auto;
    --it-timeline-pin-direction: row;
    --it-timeline-pin-icon-mr: 16px;
    --it-timeline-pin-icon-ml: 0;
    --it-timeline-pin-arrow-left: -4px;
    --it-timeline-pin-arrow-right: auto;
    --it-timeline-content-padding: 18px 18px 18px 40px;
  }
  ::slotted(it-timeline-point) {
    --it-timeline-point-content-flex-basis: 0%;
    --it-timeline-point-margin-bottom: 24px;
    --it-timeline-point-content-z-index: 1;
    --it-timeline-point-after-content: '';
    --it-timeline-point-stack-mobile-after-content: none;
  }
}
.it-timeline-point-list {
  padding-left: 0;
  list-style: none;
}

::slotted(it-timeline-point) {
  display: flex;
  width: 100%;
}`;

/**
 * Contenitore universale per entrambe le varianti di timeline di Bootstrap Italia.
 *
 * La variante è determinata dall'attributo `variant`:
 * - `classic` (default) — timeline classica con pin e card, layout `.row > .col-12`.
 * - `point-list` — lista compatta temporale, layout `ol/ul.it-timeline-point-list`.
 *
 * Usa **shadow DOM** con slot reali.
 *
 * @attr {string} variant - `classic` (default) o `point-list`.
 * @attr {string} color - Solo per `point-list`: colore predefinito per tutti i punti figlio che non ne specificano uno. Default: `primary`.
 * @attr {string} as - Solo per `point-list`: `ol` (default) o `ul`.
 * @attr {boolean} stack-mobile - Solo per `point-list`: laterale sopra il contenuto su mobile.
 *
 * @slot - Per `classic`: `<it-timeline-element>`. Per `point-list`: `<it-timeline-point>`.
 *
 * @example Timeline classica
 * ```html
 * <it-timeline>
 *   <it-timeline-element variant="past" date="MAGGIO 2026">
 *     <it-icon slot="pin-icon" name="it-file" aria-hidden="true"></it-icon>
 *     <it-card>
 *       <a slot="title" href="#">Titolo</a>
 *       <span slot="text">Descrizione.</span>
 *     </it-card>
 *   </it-timeline-element>
 * </it-timeline>
 * ```
 *
 * @example Timeline point list
 * ```html
 * <it-timeline variant="point-list">
 *   <it-timeline-point>
 *     <time slot="date" datetime="2025-10-14">
 *       <span class="visually-hidden">14 ottobre 2025</span>
 *       <span class="point-visual" aria-hidden="true">
 *         <span class="point-main">14</span>
 *         <span class="point-bottom">ott</span>
 *       </span>
 *     </time>
 *     <p slot="content">Lorem ipsum dolor sit amet.</p>
 *   </it-timeline-point>
 * </it-timeline>
 * ```
 */
let ItTimeline = class ItTimeline extends BaseComponent {
    constructor() {
        super(...arguments);
        /** Variante: `classic` (default) o `point-list`. */
        this.variant = 'classic';
        /**
         * Colore predefinito per i punti figlio — solo per `variant="point-list"`.
         * Viene propagato a tutti gli `<it-timeline-point>` che non hanno un attributo
         * `color` esplicito. Default: `primary`.
         */
        this.color = 'primary';
        /** Tag della lista — solo per `variant="point-list"`. Default: `ol`. */
        this.as = 'ol';
        /** Layout mobile verticale — solo per `variant="point-list"`. */
        this.stackMobile = false;
        /**
         * Compatta ogni elemento it-timeline-point figlio se `true` e non ha un compact esplicito. Solo per `variant="point-list"`.
         */
        this.compact = false;
        /** Propagates container color to child points and sets position on elements. */
        this._onSlotChange = () => {
            const slot = this.shadowRoot?.querySelector('slot');
            if (!slot)
                return;
            const nodes = slot.assignedElements();
            if (this._getVariant() !== 'point-list')
                return;
            const defaultColor = TIMELINE_POINT_COLORS.includes(this.color) ? this.color : 'primary';
            for (const node of nodes) {
                if (node.tagName.toLowerCase() === 'it-timeline-point') {
                    const point = node;
                    if (!point._hasExplicitColor) {
                        point.color = defaultColor;
                    }
                    if (this.compact && !point._hasExplicitCompact) {
                        point.compact = this.compact;
                    }
                    // Propagate stack-mobile so timeline-point.scss can use :host([stack-mobile])
                    point.toggleAttribute('stack-mobile', this.stackMobile);
                }
            }
        };
    }
    updated() {
        // Re-propagate color when the color property changes
        this._onSlotChange();
    }
    _getVariant() {
        return TIMELINE_VARIANTS.includes(this.variant) ? this.variant : 'classic';
    }
    render() {
        return this._getVariant() === 'point-list' ? this._renderPointList() : this._renderClassic();
    }
    _renderClassic() {
        return html `
      <div class="it-timeline-wrapper it-timeline-wrapper--classic">
        <div class="row">
          <slot @slotchange=${this._onSlotChange}></slot>
        </div>
      </div>
    `;
    }
    _renderPointList() {
        const tag = unsafeStatic(TIMELINE_POINT_LIST_TAGS.includes(this.as) ? this.as : 'ol');
        const classes = this.composeClass('it-timeline-point-list', {
            'it-timeline-point-list-stack-mobile': this.stackMobile,
        });
        return html$1 `
      <div class="it-timeline-wrapper">
        <div class="row">
          <${tag} class="${classes}" role="list">
            <slot @slotchange=${this._onSlotChange}></slot>
          </${tag}>
        </div>
      </div>
    `;
    }
};
ItTimeline.styles = styles;
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItTimeline.prototype, "variant", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItTimeline.prototype, "color", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItTimeline.prototype, "as", void 0);
__decorate([
    property({ type: Boolean, attribute: 'stack-mobile', reflect: true }),
    __metadata("design:type", Object)
], ItTimeline.prototype, "stackMobile", void 0);
__decorate([
    property({ type: Boolean, attribute: 'compact', reflect: true }),
    __metadata("design:type", Object)
], ItTimeline.prototype, "compact", void 0);
ItTimeline = __decorate([
    customElement('it-timeline')
], ItTimeline);

export { ItTimeline };
//# sourceMappingURL=it-timeline.js.map
